import type { TransactionHistoryItem } from '@/context/types';
import type { NotificationItem } from '@/types/notification.types';

export type TransactionState = {
  notifications: NotificationItem[];
  vaultPubkey?: string | null;
  pendingTransaction?: TransactionHistoryItem;
  pendingTransactions?: TransactionHistoryItem[];
  clearedNotificationIds?: string[];
  clearedTransactionIds?: string[];
};

export const notificationStorageKey = (vaultPubkey: string) => `notifications-${vaultPubkey.slice(0, 8)}`;

export function hasLiveTransaction(state: Pick<TransactionState, 'pendingTransaction' | 'pendingTransactions' | 'notifications'>): boolean {
  const pending = state.pendingTransaction;
  return Boolean(
    state.pendingTransactions?.some((tx) => !isTerminal(tx)) ||
      (pending && pending.status !== 'Completed' && pending.status !== 'Dropped') ||
      state.notifications.some((item) => item.type === 'progress' && item.metadata?.txid && !isSwapNotification(item)),
  );
}

export function isSwapNotification(item: NotificationItem): boolean {
  return Boolean(item.metadata?.swapFlow || item.metadata?.actionType === 'swap' || item.actionButtons?.some((button) => button.appAction));
}

export function clearStaleTransactionState<T extends TransactionState>(state: T): T {
  const current = applyTransactionTombstones(state);
  // Only discard unsigned form progress. A transaction hash or resumable swap
  // is recovery evidence even when its confirmation status is stale.
  const removed = current.notifications.filter((item) => item.type === 'progress' && !item.metadata?.txid && !isSwapNotification(item));
  return {
    ...current,
    notifications: current.notifications.filter((item) => !removed.includes(item)),
    clearedNotificationIds: [...new Set([...(current.clearedNotificationIds ?? []), ...removed.map(notificationRecordKey)])],
  };
}

// A reusable UI id identifies a notification slot, not a particular attempt.
export const notificationRecordKey = (item: NotificationItem) => JSON.stringify([item.id, item.instanceId ?? item.createdAt ?? null]);
const isTerminal = (tx: TransactionHistoryItem | undefined) => tx?.status === 'Completed' || tx?.status === 'Dropped';

export function parseStoredNotifications(raw: string | null): { state: TransactionState; version?: number } | undefined {
  try {
    const value = raw ? JSON.parse(raw) : undefined;
    if (!value?.state || !Array.isArray(value.state.notifications)) return undefined;
    return {
      ...value,
      state: {
        ...value.state,
        clearedNotificationIds: Array.isArray(value.state.clearedNotificationIds)
          ? value.state.clearedNotificationIds.filter((id: unknown) => typeof id === 'string')
          : [],
        clearedTransactionIds: Array.isArray(value.state.clearedTransactionIds)
          ? value.state.clearedTransactionIds.filter((id: unknown) => typeof id === 'string')
          : [],
        notifications: value.state.notifications.filter((item: unknown) => item && typeof item === 'object'),
        pendingTransactions: Array.isArray(value.state.pendingTransactions)
          ? value.state.pendingTransactions.filter((tx: TransactionHistoryItem) => tx && typeof tx.transactionId === 'string')
          : [],
        pendingTransaction:
          value.state.pendingTransaction && typeof value.state.pendingTransaction.transactionId === 'string'
            ? value.state.pendingTransaction
            : undefined,
      },
    };
  } catch {
    return undefined;
  }
}

// A stale tab may mark an older swap viewed after another tab signs the next leg.
// Progress is monotonic within one notification instance; a retry gets a new instance.
function laterNotification(current: NotificationItem, stored: NotificationItem): NotificationItem {
  if (current.type === 'success' && stored.type !== 'success') return current;
  if (stored.type === 'success' && current.type !== 'success') return stored;
  if (current.metadata?.swapFlow && current.metadata.swapFlow === stored.metadata?.swapFlow) {
    const currentStage = Number(current.metadata.swapStage?.split('_')[0] ?? 0);
    const storedStage = Number(stored.metadata.swapStage?.split('_')[0] ?? 0);
    if (storedStage > currentStage) return stored;
    if (currentStage > storedStage) return current;
    // A verified read can recover this exact submitted deposit from an old
    // outage error. Keep that recovery across tabs, but retain normal terminal
    // precedence for later errors in the same recovery revision.
    if (
      current.metadata.wrapIntentId &&
      current.metadata.wrapIntentId === stored.metadata.wrapIntentId &&
      current.metadata.wrapTxid &&
      current.metadata.wrapTxid === stored.metadata.wrapTxid
    ) {
      const currentRecovery = current.metadata.bridgeRecoveryRevision ?? 0;
      const storedRecovery = stored.metadata.bridgeRecoveryRevision ?? 0;
      if (currentRecovery > storedRecovery) return current;
      if (storedRecovery > currentRecovery) return stored;
    }
  }
  const isTerminalNotification = (item: NotificationItem) => item.type === 'error' || item.type === 'dropped';
  if (isTerminalNotification(current) && !isTerminalNotification(stored)) return current;
  if (isTerminalNotification(stored) && !isTerminalNotification(current)) return stored;
  // Submitted is informational; it must still advance to Finalizing progress.
  return current;
}

// Merge safety-bearing records from disk before every write. A tab marking an
// old notification viewed must not erase a transaction submitted by another tab.
export function applyTransactionTombstones<T extends TransactionState>(state: T, persisted?: TransactionState): T {
  const clearedNotificationIds = [...new Set([...(state.clearedNotificationIds ?? []), ...(persisted?.clearedNotificationIds ?? [])])];
  const clearedTransactionIds = [
    ...new Set([
      ...(state.clearedTransactionIds ?? []),
      ...(persisted?.clearedTransactionIds ?? []),
      ...[
        state.pendingTransaction,
        persisted?.pendingTransaction,
        ...(state.pendingTransactions ?? []),
        ...(persisted?.pendingTransactions ?? []),
      ].flatMap((tx) => (isTerminal(tx) && tx ? [tx.transactionId] : [])),
    ]),
  ];
  const isCleared = (item: NotificationItem) =>
    clearedNotificationIds.includes(notificationRecordKey(item)) ||
    // Old unscoped tombstones apply only to legacy records, never new attempts.
    (!item.instanceId && clearedNotificationIds.includes(item.id));
  const sameRecord = (a: NotificationItem, b: NotificationItem) =>
    notificationRecordKey(a) === notificationRecordKey(b) || Boolean(a.metadata?.txid && a.metadata.txid === b.metadata?.txid);
  const notifications = state.notifications.map((item) => {
    const stored = persisted?.notifications.find((other) => sameRecord(item, other));
    return stored ? laterNotification(item, stored) : item;
  });
  for (const item of persisted?.notifications ?? []) {
    if (!notifications.some((other) => sameRecord(item, other))) notifications.push(item);
  }
  let pendingTransaction = state.pendingTransaction;
  const storedPending = persisted?.pendingTransaction;
  if (
    storedPending &&
    !isTerminal(storedPending) &&
    (!pendingTransaction || (isTerminal(pendingTransaction) && pendingTransaction.transactionId !== storedPending.transactionId))
  )
    pendingTransaction = storedPending;
  if (pendingTransaction && clearedTransactionIds.includes(pendingTransaction.transactionId)) pendingTransaction = undefined;
  const pendingTransactions = [
    ...new Map(
      [
        ...(state.pendingTransactions ?? []),
        ...(persisted?.pendingTransactions ?? []),
        state.pendingTransaction,
        persisted?.pendingTransaction,
      ]
        .filter((tx): tx is TransactionHistoryItem => !!tx && !isTerminal(tx) && !clearedTransactionIds.includes(tx.transactionId))
        .map((tx) => [tx.transactionId, tx]),
    ).values(),
  ];
  if (!pendingTransaction && pendingTransactions.length) pendingTransaction = pendingTransactions.at(-1);
  return {
    ...state,
    pendingTransactions,
    clearedNotificationIds,
    clearedTransactionIds,
    notifications: notifications.filter(
      (item) =>
        !isCleared(item) && !(item.type === 'progress' && item.metadata?.txid && clearedTransactionIds.includes(item.metadata.txid)),
    ),
    pendingTransaction,
  };
}

export function resetPersistedTransactionNotifications(storage: Pick<Storage, 'getItem' | 'setItem'>, vaultPubkey: string): void {
  const key = notificationStorageKey(vaultPubkey);
  const raw = storage.getItem(key);
  if (!raw) return;
  const value = parseStoredNotifications(raw);
  if (!value) {
    storage.setItem(key, JSON.stringify({ state: { vaultPubkey, notifications: [] } }));
    return;
  }
  if (value.state?.vaultPubkey && value.state.vaultPubkey !== vaultPubkey)
    throw new Error('Stored wallet does not match the connected wallet.');
  if (!Array.isArray(value.state?.notifications)) return;
  storage.setItem(key, JSON.stringify({ ...value, state: clearStaleTransactionState(value.state) }));
}
